/**
 * @file Attachments.tsx
 * @description The attachments panel: the file rows, the version history, the
 * upload strip and every confirmation the panel raises. Consumer:
 * `AttachmentsUI`, which has already applied the PDF-export gate.
 *
 * The whole panel is a drop surface, not just the strip at its foot. A file
 * dropped on the background becomes a new attachment and goes through the
 * attachment cap and the email triage; a file dropped on a file row becomes
 * that row's next version and goes through neither, because the server permits
 * a version at the cap and because dropping an email on a row is an explicit
 * versioning act. `useAttachmentPanelDrop` owns the pointer bookkeeping and
 * registers nothing at all while the panel is read-only or busy.
 *
 * The email-triage `Popover` is always mounted and anchors on the list-and-strip
 * region rather than on the strip alone: a `.msg` can now land anywhere, and an
 * anchor the reader was nowhere near points at nothing. It deliberately does not
 * wrap the modals, the preview overlay or the message context holder — a portal
 * bubbles through the React tree, so anything inside the trigger would invoke
 * the trigger's own click handler.
 *
 * Neither cap is stated here. The 300-attachment cap is enforced by
 * `handleBatchFiles`, which only the new-file path reaches, and the per-file
 * size cap by `useAttachmentUpload`, which every upload path reaches; the
 * caption that tells the reader both lives in `AttachmentsUI`, because it was
 * liftable — the panel's parent can decide not to render a line that sits below
 * the panel.
 *
 * What is not liftable reads the export context here instead. The claim banner
 * belongs to this panel and the row controls sit inside a file row, so
 * `usePdfExportContext` is read at each of those two places. A control or a
 * suggestion to install something printed onto a rendered document is a defect,
 * not a state of the UI (SYD-2455).
 *
 * `DesktopKeepBanner` is hosted here rather than in a toolbar for one reason:
 * `DesktopKeepBannerHolders` holds a live `users` query listener for as long as
 * it is mounted, and `FormControl` and `RecordDetailPanel` are always mounted.
 * On the attachments panel it is scoped to the surface it is about. It sits
 * outside the email-triage `Popover`, whose trigger swallows clicks from
 * anything portalled inside it.
 *
 * `AttachmentHeldPushAlerts` sits beside the banner and carries no export gate
 * at this end of the wire: unlike the banner, it reads `usePdfExportContext`
 * itself and feeds the answer to its own listener, so an export tree neither
 * renders it nor subscribes.
 *
 * Both are absent, not disabled, without the `fileSync` right. Syncretic File
 * Sync is unreleased, so hiding is deliberate here rather than the usual
 * disable — and hiding at this end is also what keeps the banner's `users` query
 * and the alerts' `heldPushes` listener from opening for a reader who has no
 * agent. The version provenance tag and the "Synced" chip stay regardless: they
 * describe versions a colleague's agent may already have written, and hiding
 * them would leave a version unexplained.
 *
 * Extract schedule runs through `useExtractSchedule` with this panel's own
 * message api, and only a `form` host hands it an order; any other host's rows
 * are disabled with a reason before a click can reach the handler. An
 * extraction in flight counts as an operation in progress, so the panel does not
 * start a second action while it reads and sends the bytes. The host is passed
 * on to the preview overlay, whose header carries the same control.
 *
 * `supersededBy` is the one refusal this panel makes on its own. A version the
 * form no longer names cannot take an attachment version — `commitAttachmentVersion`
 * refuses that commit — so every path that would commit bytes is closed here
 * before they move, and the notice links the version that would accept them
 * (SYD-3049). It closes only those paths: the server still takes a delete, a
 * rename or a promotion on a superseded version, so `disabled` stays separate.
 *
 * `attachments-dragger-boundary` is a bare element for one reason: rc-upload
 * takes a drop on the strip without stopping it, so without a fence the same
 * file would upload twice. It must impose no layout, which is why it is not a
 * `FlexBox`. The fence runs the hook's `onDraggerBoundaryDrop` rather than a
 * bare `stopPropagation`, because the drop it stops is also what would have
 * taken the highlight back down, reported an empty release, and attached a
 * file held only in `items`, which the strip never reads.
 */
import { FC, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload,
  Modal,
  Typography,
  Progress,
  theme,
  message,
  Popover,
  Button,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd/es/upload/interface';
import { Timestamp } from 'firebase/firestore';

import { ArtifactAttachments } from '#types/System.types';
import {
  EMAIL_FILE_PATTERN,
  MAX_ATTACHMENTS_PER_DOCUMENT,
  MAX_COMMUNICATION_LOG_ENTRIES,
} from '#types/System.Parameters.types';
import { useAttachments } from '../../../use/attachments/useAttachments';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { AttachmentList } from './AttachmentList';
import { AttachmentHeldPushAlerts } from './AttachmentHeldPushAlerts';
import AttachmentEmailModal from '../../modals/AttachmentEmailModal';
import {
  AttachmentCommunicationRecord,
  EmailCommunicationRecord,
} from '#types';
import {
  parseEmailFile,
  ParentUploadFunction,
} from '../fields/CommunicationLog/parser/communicationParser';
import { resolveCurrentAttachmentName } from './resolveAttachmentDisplayName';
import AttachmentPreviewOverlay from './AttachmentPreviewOverlay';
import type { ReadOnlyReason } from '../readOnlyReason';
import { useAttachmentPanelDrop } from '../../../use/attachments/useAttachmentPanelDrop';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { DesktopKeepBanner } from '../../desktopSync';
import { useFileSyncAccess } from '../../../use/desktopSync/useFileSyncAccess';
import { useExtractSchedule } from '../../../use/attachments/useExtractSchedule';
import type { ExtractScheduleHost, SupersedingFormVersion } from './index.types';

const { Text } = Typography;
const { useToken } = theme;

/** The last dot-segment of a name, lower-cased, or '' when the name has none. */
const fileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot === -1 ? '' : fileName.slice(lastDot + 1).toLowerCase();
};

/** Reads an extension into the confirmation's sentence, absence included. */
const describeExtension = (extension: string): string =>
  extension ? `ends in .${extension}` : 'has no extension';

/**
 * Props for the AttachmentUI component
 */
export interface AttachmentsCommunicationLogOptions {
  parentDocId: string;
  addEmailToLog: (
    record: EmailCommunicationRecord,
  ) => Promise<{ success: boolean; truncated: boolean }>;
  addAttachmentRenameToLog?: (
    record: AttachmentCommunicationRecord,
  ) => Promise<{ success: boolean; truncated: boolean }>;
  currentLogEntryCount: number;
}

export interface RawEmailAliasContext {
  formNumber?: string;
  formVersionNumber?: number;
}

interface AttachmentsProps {
  /** Attachment map with IDs as keys and version maps as values */
  value?: ArtifactAttachments;
  /** Callback triggered when attachments change */
  onChange?: (value: ArtifactAttachments) => void;
  /** Whether attachment operations are disabled */
  disabled?: boolean;
  /**
   * Why the panel cannot be changed, when that is worth telling the reader.
   * Already final when it arrives: `AttachmentsUI` has applied the PDF export
   * gate, so this component never reasons about export mode.
   */
  readOnlyReason?: ReadOnlyReason;
  /** Optional configuration for integrating detected emails with CommunicationLog */
  communicationLogOptions?: AttachmentsCommunicationLogOptions;
  /** Optional context used to build human-readable names for raw email source files */
  rawEmailAliasContext?: RawEmailAliasContext;
  /** Where this panel is hosted, which decides whether Extract schedule can write to a form. */
  extractScheduleHost: ExtractScheduleHost;
  /**
   * The version the form names, when it is not the one this panel is on. Set
   * only on a form artifact, and only once the form document has loaded naming
   * another version — an unknown pointer refuses nothing.
   */
  supersededBy?: SupersedingFormVersion;
}

interface PendingDelete {
  fileUid: string;
  fileName: string;
}

interface PendingVersionDelete {
  fileUid: string;
  versionId: string;
  versionNumber: number;
  fileName: string;
}

/**
 * A row drop waiting on the reader, because it either renames what it stores or
 * splits into more than one thing. `newFiles` are the trailing files of the same
 * drop, which become new attachments rather than versions.
 */
interface PendingVersionDrop {
  fileUid: string;
  rowFileName: string;
  versionFile: File;
  newFiles: File[];
}

/**
 * Component for managing file attachments with Firebase storage integration.
 * Provides a UI for uploading, downloading, and deleting files with drag-and-drop support.
 */

export const Attachments: FC<AttachmentsProps> = ({
  value = {},
  onChange,
  disabled = false,
  readOnlyReason,
  communicationLogOptions,
  rawEmailAliasContext,
  extractScheduleHost,
  supersededBy,
}) => {
  const { token } = useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const { isPdfExportMode } = usePdfExportContext();
  const hasFileSyncAccess = useFileSyncAccess();

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );
  const [isVersionDeleteModalOpen, setIsVersionDeleteModalOpen] =
    useState(false);
  const [pendingVersionDelete, setPendingVersionDelete] =
    useState<PendingVersionDelete | null>(null);
  const [pendingVersionDrop, setPendingVersionDrop] =
    useState<PendingVersionDrop | null>(null);

  // Email modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailFile, setEmailFile] = useState<{
    fileName: string;
    fileContent?: string;
    contentType?: string;
  }>({ fileName: '' });

  // Email file confirmation state
  const [pendingEmailFiles, setPendingEmailFiles] = useState<File[]>([]);
  const [showEmailPopover, setShowEmailPopover] = useState(false);

  const [previewTarget, setPreviewTarget] = useState<{
    fileUid: string;
    versionId: string;
    fileName: string;
    declaredContentType?: string;
  } | null>(null);

  const handleSuccess = useCallback(
    (successMessage: string) => {
      messageApi.success(successMessage);
    },
    [messageApi],
  );

  const handleError = useCallback(
    (error: Error) => {
      messageApi.error(error.message);
    },
    [messageApi],
  );

  const {
    fileList,
    attachments,
    uploadFiles,
    uploadFileVersion,
    downloadFile,
    downloadVersion,
    cancelDownload,
    removeFile,
    removeVersion,
    publishVersion,
    shareVersion,
    unshareVersion,
    emailAttachment,
    progress,
    isUploading,
    isDownloading,
    isRemoving,
    isSharing,
    isEmailing,
    isPublishing,
    artifactPath,
    renameFile,
    isRenaming,
    updateFileNotes,
    updateFileImportance,
    updateFileLabels,
  } = useAttachments({
    value,
    onChange,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const { extractSchedule, extractingVersionId, canExtract } =
    useExtractSchedule(messageApi);

  /**
   * Whether a file may still become a version here. A superseded version is
   * refused by `commitAttachmentVersion`, so the panel refuses first; it is a
   * boolean rather than `supersededBy` itself so the gates below keep stable
   * callback identities.
   */
  const uploadsRefused = disabled || supersededBy !== undefined;

  const anyOperationInProgress =
    isUploading ||
    isDownloading ||
    isRemoving ||
    isSharing ||
    isEmailing ||
    isRenaming ||
    isPublishing ||
    extractingVersionId !== null;

  const handleExtractSchedule = useCallback(
    (fileUid: string, versionId: string, fileName: string) =>
      extractScheduleHost.kind === 'form'
        ? extractSchedule({
            fileUid,
            versionId,
            fileName,
            order: extractScheduleHost.order,
          })
        : Promise.resolve(),
    [extractSchedule, extractScheduleHost],
  );

  /** The word the desktop-sync copy uses. The path `useAttachments` resolved already says which. */
  const artifactLabel = artifactPath?.startsWith('entities/')
    ? 'record'
    : 'form';

  const handlePreviewVersion = useCallback(
    (fileUid: string, versionId: string, fileName: string) => {
      if (disabled || anyOperationInProgress) return;
      const declaredContentType =
        attachments?.[fileUid]?.versions?.[versionId]?.type;
      setPreviewTarget({ fileUid, versionId, fileName, declaredContentType });
    },
    [anyOperationInProgress, attachments, disabled],
  );

  const closePreview = useCallback(() => {
    setPreviewTarget(null);
  }, []);

  /** Uploads embedded email attachments as parent form attachments */
  const uploadEmailEmbeddedAttachment = useCallback<ParentUploadFunction>(
    async (fileToUpload, emailRecordId) => {
      try {
        const results = await uploadFiles([fileToUpload], emailRecordId);
        if (results?.length) {
          return results[0];
        }
        console.warn(
          `Embedded file ${fileToUpload.name} uploaded, but metadata was unavailable.`,
        );
        return null;
      } catch (error) {
        console.error(
          `Attachments >> Failed uploading embedded attachment ${fileToUpload.name}`,
          error,
        );
        handleError(
          error instanceof Error
            ? error
            : new Error(`Failed to upload ${fileToUpload.name}`),
        );
        return null;
      }
    },
    [uploadFiles, handleError],
  );

  /** Uploads email files as regular attachments without parsing */
  const handleEmailFileUploadOnly = useCallback(
    async (emailFiles: File[]) => {
      try {
        await uploadFiles(emailFiles);
        setShowEmailPopover(false);
        setPendingEmailFiles([]);
      } catch (error) {
        console.error('Attachments >> Failed to upload email files', error);
        handleError(
          error instanceof Error ? error : new Error('Failed to upload files'),
        );
      }
    },
    [uploadFiles, handleError],
  );

  /**
   * Uploads email files as attachments and parses them for the communication log.
   * Each email file is saved as an attachment and its contents are parsed and added to the log.
   */
  const handleEmailFileAddToLog = useCallback(
    async (emailFiles: File[]) => {
      if (!communicationLogOptions || !communicationLogOptions.parentDocId) {
        return;
      }

      const remainingLogSlots =
        MAX_COMMUNICATION_LOG_ENTRIES -
        communicationLogOptions.currentLogEntryCount;

      if (remainingLogSlots <= 0) {
        messageApi.error(
          `Maximum communication log entries reached (${MAX_COMMUNICATION_LOG_ENTRIES}). Please delete some entries before adding emails.`,
        );
        setShowEmailPopover(false);
        setPendingEmailFiles([]);
        return;
      }

      if (emailFiles.length > remainingLogSlots) {
        messageApi.warning(
          `Can only add ${remainingLogSlots} more email${remainingLogSlots === 1 ? '' : 's'} to log. Current: ${communicationLogOptions.currentLogEntryCount}/${MAX_COMMUNICATION_LOG_ENTRIES}`,
        );
        setShowEmailPopover(false);
        setPendingEmailFiles([]);
        return;
      }

      try {
        let successCount = 0;
        let failCount = 0;

        for (const file of emailFiles) {
          try {
            const emailDate = new Date();
            const timestampSuffix = emailDate.toISOString();
            const fileSuffix = file.name
              .replace(/[^a-zA-Z0-9_]/g, '')
              .slice(0, 50);
            const emailRecordId = `${communicationLogOptions.parentDocId}#EMAIL#${timestampSuffix}_${fileSuffix}#${crypto.randomUUID()}`;

            try {
              await uploadFiles([file], emailRecordId);
            } catch (uploadError) {
              console.warn(
                `Attachments >> Failed to upload original email file ${file.name}, but continuing with parse`,
                uploadError,
              );
            }

            const emailRecord = await parseEmailFile(
              file,
              uploadEmailEmbeddedAttachment,
              communicationLogOptions.parentDocId,
              emailRecordId,
            );

            const result =
              await communicationLogOptions.addEmailToLog(emailRecord);
            if (result?.success) {
              successCount++;
              if (emailFiles.length === 1) {
                messageApi.success(
                  `Email "${emailRecord.subject || file.name}" saved and added to Communication Log`,
                );
              }
              if (result.truncated) {
                messageApi.warning(
                  'Email body truncated to satisfy log size limits',
                );
              }
            } else {
              failCount++;
              if (emailFiles.length === 1) {
                messageApi.error('Failed to add email to Communication Log');
              }
            }
          } catch (error) {
            failCount++;
            console.error(
              `Attachments >> Failed to process email file ${file.name}`,
              error,
            );
            if (emailFiles.length === 1) {
              messageApi.error(`Could not process ${file.name || 'email'}`);
            }
          }
        }

        if (emailFiles.length > 1) {
          if (successCount > 0) {
            messageApi.success(
              `${successCount} email${successCount === 1 ? '' : 's'} saved and added to Communication Log`,
            );
          }
          if (failCount > 0) {
            messageApi.error(
              `Failed to process ${failCount} email${failCount === 1 ? '' : 's'}`,
            );
          }
        }

        setShowEmailPopover(false);
        setPendingEmailFiles([]);
      } catch (error) {
        console.error('Attachments >> Failed to process email files', error);
        handleError(
          error instanceof Error ? error : new Error('Failed to process files'),
        );
      }
    },
    [
      communicationLogOptions,
      uploadFiles,
      uploadEmailEmbeddedAttachment,
      messageApi,
      handleError,
    ],
  );

  /** Cancels the email file upload confirmation popover */
  const handleEmailFileCancel = useCallback(() => {
    setShowEmailPopover(false);
    setPendingEmailFiles([]);
  }, []);

  /**
   * Processes uploaded files, separating email files from regular files.
   * Email files trigger a confirmation popover when communicationLogOptions is configured.
   */
  const handleBatchFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || uploadsRefused || anyOperationInProgress) return;

      const currentAttachmentCount = Object.keys(attachments).length;
      const remainingSlots =
        MAX_ATTACHMENTS_PER_DOCUMENT - currentAttachmentCount;

      if (currentAttachmentCount >= MAX_ATTACHMENTS_PER_DOCUMENT) {
        messageApi.error(
          `Maximum attachment limit reached (${MAX_ATTACHMENTS_PER_DOCUMENT}). Please delete some attachments before uploading new ones.`,
        );
        return;
      }

      if (files.length > remainingSlots) {
        messageApi.warning(
          `Can only upload ${remainingSlots} more attachment${remainingSlots === 1 ? '' : 's'}. Current: ${currentAttachmentCount}/${MAX_ATTACHMENTS_PER_DOCUMENT}`,
        );
        return;
      }

      if (!communicationLogOptions) {
        const emailFilesDetected = files.filter((file) =>
          EMAIL_FILE_PATTERN.test((file.name || '').toLowerCase()),
        );
        if (emailFilesDetected.length > 0) {
          console.error(
            'Attachments >> Email files uploaded as regular attachments (Communication Log not configured):',
            emailFilesDetected.map((f) => f.name).join(', '),
          );
        }
        try {
          await uploadFiles(files);
        } catch (error) {
          console.warn('Attachments >> Failed to upload files', error);
          handleError(
            error instanceof Error
              ? error
              : new Error('Failed to upload files'),
          );
        }
        return;
      }

      const emailFiles = files.filter((file) =>
        EMAIL_FILE_PATTERN.test((file.name || '').toLowerCase()),
      );
      const regularFiles = files.filter(
        (file) => !EMAIL_FILE_PATTERN.test((file.name || '').toLowerCase()),
      );

      try {
        if (regularFiles.length > 0) {
          await uploadFiles(regularFiles);
        }

        if (emailFiles.length > 0) {
          setPendingEmailFiles(emailFiles);
          setShowEmailPopover(true);
        }
      } catch (error) {
        console.error('Attachments >> Failed to upload files', error);
        handleError(
          error instanceof Error ? error : new Error('Failed to upload files'),
        );
      }
    },
    [
      uploadsRefused,
      anyOperationInProgress,
      uploadFiles,
      communicationLogOptions,
      handleError,
      attachments,
      messageApi,
    ],
  );

  /**
   * Uploads the drop's first file as the row's next version and routes the
   * trailing files through the normal new-file path. The row's own name is
   * what the version is reserved under, so an entry that predates SYD-2457
   * and carries no top-level `fileName` is not renamed by the drop. The
   * attachment cap and the email triage are both skipped: neither describes
   * what a version does.
   */
  const runVersionDrop = useCallback(
    async (drop: PendingVersionDrop) => {
      await uploadFileVersion(drop.fileUid, drop.versionFile, drop.rowFileName);
      if (drop.newFiles.length > 0) {
        await handleBatchFiles(drop.newFiles);
      }
    },
    [handleBatchFiles, uploadFileVersion],
  );

  /**
   * Takes a drop that landed on a file row. Two of its outcomes are not what
   * the pointer literally said, so both are confirmed first: a dropped file
   * whose extension differs from the row's makes a version whose name does not
   * describe its bytes, and a drop carrying several files versions only the
   * first. Everything else proceeds silently.
   */
  const handleRowVersionDrop = useCallback(
    async (fileUid: string, files: File[]) => {
      const [versionFile, ...newFiles] = files;
      const rowFileName = resolveCurrentAttachmentName(attachments[fileUid]);

      if (!versionFile || !rowFileName) {
        await handleBatchFiles(files);
        return;
      }

      const drop: PendingVersionDrop = {
        fileUid,
        rowFileName,
        versionFile,
        newFiles,
      };
      const renamesWhatItStores =
        fileExtension(rowFileName) !== fileExtension(versionFile.name);

      if (!renamesWhatItStores && newFiles.length === 0) {
        await runVersionDrop(drop);
        return;
      }

      setPendingVersionDrop(drop);
    },
    [attachments, handleBatchFiles, runVersionDrop],
  );

  /**
   * Runs the confirmed row drop. The panel may have gone read-only or busy
   * while the confirmation stood open, so both are read here rather than at
   * the moment of the drop.
   */
  const handleVersionDropConfirm = useCallback(async () => {
    if (!pendingVersionDrop || uploadsRefused || anyOperationInProgress) return;
    const drop = pendingVersionDrop;
    setPendingVersionDrop(null);
    await runVersionDrop(drop);
  }, [pendingVersionDrop, uploadsRefused, anyOperationInProgress, runVersionDrop]);

  const handleVersionDropCancel = useCallback(() => {
    setPendingVersionDrop(null);
  }, []);

  /** A drop on a file row versions that row; a drop anywhere else attaches. */
  const handlePanelDrop = useCallback(
    (files: File[], targetFileUid: string | null) => {
      if (uploadsRefused || anyOperationInProgress) return;
      if (targetFileUid === null) {
        void handleBatchFiles(files);
        return;
      }
      void handleRowVersionDrop(targetFileUid, files);
    },
    [uploadsRefused, anyOperationInProgress, handleBatchFiles, handleRowVersionDrop],
  );

  /**
   * A release that advertised files and carried none. Nothing uploads and
   * nothing fails, so the panel is the only thing that can tell the reader the
   * drag arrived empty and what to do about it.
   */
  const handleEmptyDrop = useCallback(() => {
    messageApi.warning(
      'That drop carried no file. A file kept online or on a network drive can arrive empty — open it once so it downloads, then drag it again, or click to browse.',
    );
  }, [messageApi]);

  const {
    isDragging,
    dropTargetFileUid,
    containerHandlers,
    onDraggerBoundaryDrop,
    onRowDragEnter,
    onRowDragLeave,
  } = useAttachmentPanelDrop({
    enabled: !uploadsRefused && !anyOperationInProgress,
    onFilesDropped: handlePanelDrop,
    onEmptyDrop: handleEmptyDrop,
  });

  const handleDownload = useCallback(
    async (fileUid: string, fileName: string) => {
      if (disabled || anyOperationInProgress) return;
      await downloadFile(fileUid, fileName);
    },
    [disabled, anyOperationInProgress, downloadFile],
  );

  const handleRenameFile = useCallback(
    async (fileUid: string, newFileName: string) => {
      if (disabled || anyOperationInProgress) return;

      const previousFileName = resolveCurrentAttachmentName(
        attachments[fileUid],
      );
      const renameResult = await renameFile(fileUid, newFileName);

      if (
        !renameResult?.success ||
        !communicationLogOptions?.addAttachmentRenameToLog
      ) {
        return renameResult;
      }

      const normalizedNewName = newFileName.trim();
      if (
        !previousFileName ||
        !normalizedNewName ||
        normalizedNewName === previousFileName
      ) {
        return renameResult;
      }

      const now = Timestamp.now();
      const renameRecord: AttachmentCommunicationRecord = {
        type: 'attachment',
        id: crypto.randomUUID(),
        key: crypto.randomUUID(),
        timestamp: now,
        createdAt: now,
        updatedAt: now,
        fileUid,
        previousFileName,
        nextFileName: normalizedNewName,
      };

      try {
        const logResult =
          await communicationLogOptions.addAttachmentRenameToLog(renameRecord);
        if (!logResult.success) {
          messageApi.warning(
            'Attachment renamed, but failed to add activity log entry.',
          );
        }
      } catch {
        messageApi.warning(
          'Attachment renamed, but failed to add activity log entry.',
        );
      }

      return renameResult;
    },
    [
      disabled,
      anyOperationInProgress,
      attachments,
      renameFile,
      communicationLogOptions,
      messageApi,
    ],
  );

  const handleVersionDownload = useCallback(
    async (fileUid: string, versionId: string, fileName: string) => {
      if (disabled || anyOperationInProgress) return;
      await downloadVersion(fileUid, versionId, fileName);
    },
    [disabled, anyOperationInProgress, downloadVersion],
  );

  /**
   * Publishes the file's current save. The panel picks the promoted version
   * up through the artifact document's live subscription.
   */
  const handlePublishVersion = useCallback(
    async (fileUid: string, versionId: string, fileName: string) => {
      if (disabled || anyOperationInProgress) return;
      await publishVersion(fileUid, versionId, fileName);
    },
    [disabled, anyOperationInProgress, publishVersion],
  );

  const handleDelete = useCallback(
    (fileUid: string, fileName: string) => {
      if (disabled || anyOperationInProgress) return;
      setPendingDelete({ fileUid, fileName });
      setIsDeleteModalOpen(true);
    },
    [disabled, anyOperationInProgress],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDelete || anyOperationInProgress) return;
    await removeFile(pendingDelete.fileUid, pendingDelete.fileName);
    setIsDeleteModalOpen(false);
    setPendingDelete(null);
  }, [pendingDelete, removeFile, anyOperationInProgress]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalOpen(false);
    setPendingDelete(null);
  }, []);

  const handleVersionDelete = useCallback(
    (
      fileUid: string,
      versionId: string,
      versionNumber: number,
      fileName: string,
    ) => {
      if (disabled || anyOperationInProgress) return;
      setPendingVersionDelete({ fileUid, versionId, versionNumber, fileName });
      setIsVersionDeleteModalOpen(true);
    },
    [disabled, anyOperationInProgress],
  );

  const handleVersionDeleteConfirm = useCallback(async () => {
    if (!pendingVersionDelete || anyOperationInProgress) return;
    await removeVersion(
      pendingVersionDelete.fileUid,
      pendingVersionDelete.versionId,
      pendingVersionDelete.fileName,
    );
    setIsVersionDeleteModalOpen(false);
    setPendingVersionDelete(null);
  }, [pendingVersionDelete, removeVersion, anyOperationInProgress]);

  const handleVersionDeleteCancel = useCallback(() => {
    setIsVersionDeleteModalOpen(false);
    setPendingVersionDelete(null);
  }, []);

  const handleShareVersion = useCallback(
    async (fileUid: string, versionId: string, fileName: string) => {
      if (disabled || anyOperationInProgress) return;

      if (!artifactPath) {
        messageApi.error('Cannot create share link: No file context available');
        return;
      }

      const shareResult = await shareVersion(fileUid, versionId, fileName);

      try {
        await navigator.clipboard.writeText(shareResult?.shareUrl || '');
        messageApi.success('Share link created and copied to clipboard');
      } catch (error) {
        messageApi.error('Share link created but failed to copy to clipboard');
      }
    },
    [disabled, anyOperationInProgress, shareVersion, artifactPath, messageApi],
  );

  /**
   * Handles revoking a share link for a file version
   * Explicitly passes artifactPath to ensure proper handling
   */
  const handleUnshareVersion = useCallback(
    async (fileUid: string, versionId: string) => {
      if (disabled || anyOperationInProgress) return;

      if (!artifactPath) {
        messageApi.error('Cannot revoke share link: No file context available');
        return;
      }

      await unshareVersion(fileUid, versionId, artifactPath);
    },
    [
      disabled,
      anyOperationInProgress,
      unshareVersion,
      artifactPath,
      messageApi,
    ],
  );

  const handleCopyShareLink = useCallback(
    async (fileUid: string, versionId: string) => {
      if (disabled || anyOperationInProgress) return;

      const versionData = attachments[fileUid]?.versions?.[versionId];
      if (!versionData?.share?.url) {
        messageApi.error('No share link available to copy');
        return;
      }

      try {
        await navigator.clipboard.writeText(versionData.share.url);
        messageApi.success('Share link copied to clipboard');
      } catch (error) {
        messageApi.error('Failed to copy share link to clipboard');
      }
    },
    [disabled, anyOperationInProgress, attachments, messageApi],
  );

  /**
   * Handler for emailing a file version
   */
  const handleEmailVersion = useCallback(
    async (fileUid: string, versionId: string, fileName: string) => {
      if (disabled || anyOperationInProgress) return;

      try {
        const result = await emailAttachment(fileUid, versionId, fileName);

        if (result.success && result.fileName && result.base64Content) {
          setEmailFile({
            fileName: result.fileName,
            fileContent: result.base64Content,
            contentType: result.contentType,
          });
          setIsEmailModalOpen(true);
        }
      } catch (error) {
        console.error('Error preparing email:', error);
        handleError(
          error instanceof Error ? error : new Error('Failed to prepare email'),
        );
      }
    },
    [disabled, anyOperationInProgress, emailAttachment, handleError],
  );

  const uploadProps: UploadProps = {
    customRequest: () => {},
    fileList,
    multiple: true,
    disabled: uploadsRefused || anyOperationInProgress,
    listType: 'text',
    onRemove: () => false,
    directory: false,
    beforeUpload: (file, fileList) => {
      if (fileList.length === 1) {
        handleBatchFiles([file]);
      } else if (file === fileList[fileList.length - 1]) {
        handleBatchFiles(fileList);
      }
      return false;
    },
    progress: {
      strokeColor: {
        '0%': token.colorPrimary,
        '100%': token.colorSuccess,
      },
      strokeWidth: 2,
      format: (percent?: number) => `${parseFloat((percent || 0).toFixed(2))}%`,
      style: {
        width: '100%',
        top: 0,
        padding: '4px 0',
      },
    },
    style: {
      borderRadius: token.borderRadiusLG,
      width: '100%',
      minWidth: '100%',
    },
    showUploadList: false,
  };

  /**
   * The strip's icon and label are primary-coloured only where no
   * communication log is configured. Preserved as it was when the two
   * draggers were separate components.
   */
  const draggerAccentColor = communicationLogOptions
    ? undefined
    : token.colorPrimary;

  const emailPopoverContent = (
    <FlexCol gap={20}>
      <FlexCol gap={0}>
        <Text strong>
          {pendingEmailFiles.length === 1
            ? `Email file detected (${pendingEmailFiles[0]?.name})`
            : `${pendingEmailFiles.length} email files detected`}
        </Text>
        <Text>
          What would you like to do with{' '}
          {pendingEmailFiles.length === 1 ? 'this file' : 'these files'}?
        </Text>
      </FlexCol>
      <FlexCol>
        <Button
          type="primary"
          block
          onClick={() => handleEmailFileAddToLog(pendingEmailFiles)}
          disabled={uploadsRefused || anyOperationInProgress}
          aria-label="Add email to communication log"
        >
          Add to Communication Log
        </Button>
        <Button
          block
          onClick={() => handleEmailFileUploadOnly(pendingEmailFiles)}
          disabled={uploadsRefused || anyOperationInProgress}
          aria-label="Upload as attachment only"
        >
          Save as Attachment Only
        </Button>
        <Button
          block
          onClick={handleEmailFileCancel}
          disabled={anyOperationInProgress}

          type="link"
          aria-label="Cancel upload"
        >
          Cancel
        </Button>
      </FlexCol>
    </FlexCol>
  );

  return (
    <FlexCol
      stretch
      style={{
        width: '100%',
        alignItems: 'stretch',
        borderRadius: token.borderRadiusLG,
        outline:
          isDragging && dropTargetFileUid === null
            ? `${token.lineWidth}px dashed ${token.colorPrimary}`
            : undefined,
      }}
      id="attachments-container"
      {...containerHandlers}
    >
      {contextHolder}
      {supersededBy && (
        <Text id="attachments-superseded-notice" type="warning">
          This version is no longer the current version of this form, so it
          cannot take new files. Attach them to{' '}
          <Link
            to={`/forms/${supersededBy.formId}/formVersions/${supersededBy.currentVersionId}`}
            aria-label="Open the current version of this form"
          >
            the current version
          </Link>{' '}
          instead.
        </Text>
      )}
      {readOnlyReason === 'noWriteAccess' && (
        <Text id="attachments-read-only-notice" type="secondary">
          You can read this document's files but not change them. Downloads and
          previews still work.
        </Text>
      )}
      {hasFileSyncAccess && !isPdfExportMode && (
        <DesktopKeepBanner
          artifactPath={artifactPath}
          artifactLabel={artifactLabel}
        />
      )}
      {hasFileSyncAccess && (
        <AttachmentHeldPushAlerts
          artifactPath={artifactPath}
          attachments={attachments}
        />
      )}
      <Popover
        content={emailPopoverContent}
        open={showEmailPopover}
        trigger="click"
        placement="bottom"
        onOpenChange={(visible) => {
          if (!visible) {
            handleEmailFileCancel();
          }
        }}
      >
        <FlexCol
          stretch
          style={{
            width: '100%',
            alignItems: 'stretch',
          }}
          id="attachments-files"
        >
          <AttachmentList
            fileList={fileList}
            attachments={attachments}
            artifactPath={artifactPath}
            anyOperationInProgress={anyOperationInProgress}
            disabled={disabled}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onVersionDelete={handleVersionDelete}
            onVersionDownload={handleVersionDownload}
            onPublishVersion={handlePublishVersion}
            onShareVersion={handleShareVersion}
            onUnshareVersion={handleUnshareVersion}
            onCopyShareLink={handleCopyShareLink}
            onEmailVersion={handleEmailVersion}
            onPreviewVersion={handlePreviewVersion}
            onRenameFile={handleRenameFile}
            onUpdateFileNotes={updateFileNotes}
            onUpdateFileImportance={updateFileImportance}
            onUpdateFileLabels={updateFileLabels}
            rawEmailAliasContext={rawEmailAliasContext}
            dropTargetFileUid={dropTargetFileUid}
            onRowDragEnter={onRowDragEnter}
            onRowDragLeave={onRowDragLeave}
            extractScheduleHost={extractScheduleHost}
            canExtractSchedule={canExtract}
            extractingVersionId={extractingVersionId}
            onExtractSchedule={handleExtractSchedule}
          />
          {!disabled && (
            <div
              id="attachments-dragger-boundary"
              onDrop={onDraggerBoundaryDrop}
            >
              <Upload.Dragger
                {...uploadProps}
                id="attachments-dragger"
                aria-label="Upload attachments area"
              >
                <FlexBox
                  justifyCenter
                  id="attachments-upload-content"
                  style={{
                    margin: -10,
                  }}
                >
                  {!isDownloading && (
                    <UploadOutlined
                      style={{
                        fontSize: 25,
                        color: draggerAccentColor,
                      }}
                    />
                  )}
                  <Text
                    style={{
                      color: draggerAccentColor,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isDownloading
                      ? `${progress}%`
                      : 'Click or drop files to attach'}
                  </Text>
                  {isDownloading && (
                    <Button
                      type="link"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        cancelDownload();
                      }}
                      aria-label="Cancel download"
                    >
                      Cancel
                    </Button>
                  )}
                </FlexBox>
              </Upload.Dragger>
            </div>
          )}
        </FlexCol>
      </Popover>

      <Modal
        title="Confirm Delete"
        open={isDeleteModalOpen}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          disabled: anyOperationInProgress,
          'aria-label': `Confirm delete ${pendingDelete?.fileName || 'file'}`,
        }}
        cancelButtonProps={{
          disabled: anyOperationInProgress,
          'aria-label': 'Cancel delete operation',
        }}
      >
        <p>Are you sure you want to delete "{pendingDelete?.fileName}"?</p>
        <p>This action cannot be undone.</p>
        {anyOperationInProgress && (
          <Progress percent={progress} status="active" />
        )}
      </Modal>

      <Modal
        title="Confirm Version Delete"
        open={isVersionDeleteModalOpen}
        onOk={handleVersionDeleteConfirm}
        onCancel={handleVersionDeleteCancel}
        okText="Delete Version"
        cancelText="Cancel"
        okButtonProps={{
          disabled: anyOperationInProgress,
          'aria-label': `Confirm delete version ${pendingVersionDelete?.versionNumber || ''} of ${pendingVersionDelete?.fileName || 'file'}`,
        }}
        cancelButtonProps={{
          disabled: anyOperationInProgress,
          'aria-label': 'Cancel delete version operation',
        }}
      >
        <p>
          Are you sure you want to delete version{' '}
          {pendingVersionDelete?.versionNumber} of "
          {pendingVersionDelete?.fileName}"?
        </p>
        <p>This action cannot be undone.</p>
        {anyOperationInProgress && (
          <Progress percent={progress} status="active" />
        )}
      </Modal>

      <Modal
        title="Confirm New Version"
        open={pendingVersionDrop !== null}
        onOk={handleVersionDropConfirm}
        onCancel={handleVersionDropCancel}
        okText="Add version"
        cancelText="Cancel"
        okButtonProps={{
          disabled: uploadsRefused || anyOperationInProgress,
          'aria-label': `Add version of ${pendingVersionDrop?.rowFileName || 'file'}`,
        }}
        cancelButtonProps={{
          'aria-label': 'Cancel adding a version',
        }}
      >
        <FlexCol>
          <Text>
            "{pendingVersionDrop?.versionFile.name}" is added to "
            {pendingVersionDrop?.rowFileName}" as its next version.
          </Text>
          {pendingVersionDrop &&
            fileExtension(pendingVersionDrop.rowFileName) !==
              fileExtension(pendingVersionDrop.versionFile.name) && (
              <Text>
                "{pendingVersionDrop.rowFileName}"{' '}
                {describeExtension(
                  fileExtension(pendingVersionDrop.rowFileName),
                )}{' '}
                and "{pendingVersionDrop.versionFile.name}"{' '}
                {describeExtension(
                  fileExtension(pendingVersionDrop.versionFile.name),
                )}
                . The version is stored under "{pendingVersionDrop.rowFileName}
                ", so its name will not describe its contents.
              </Text>
            )}
          {pendingVersionDrop && pendingVersionDrop.newFiles.length > 0 && (
            <Text>
              The other {pendingVersionDrop.newFiles.length}{' '}
              {pendingVersionDrop.newFiles.length === 1
                ? 'file is'
                : 'files are'}{' '}
              added as new{' '}
              {pendingVersionDrop.newFiles.length === 1
                ? 'attachment'
                : 'attachments'}
              , not as versions.
            </Text>
          )}
          {anyOperationInProgress && (
            <Progress percent={progress} status="active" />
          )}
        </FlexCol>
      </Modal>

      <AttachmentEmailModal
        open={isEmailModalOpen}
        onCancel={() => setIsEmailModalOpen(false)}
        fileName={emailFile.fileName}
        fileContent={emailFile.fileContent}
        contentType={emailFile.contentType}
        aria-label="Email attachment modal"
      />

      <AttachmentPreviewOverlay
        open={Boolean(previewTarget)}
        onClose={closePreview}
        fileUid={previewTarget?.fileUid}
        versionId={previewTarget?.versionId}
        fileName={previewTarget?.fileName}
        declaredContentType={previewTarget?.declaredContentType}
        extractScheduleHost={extractScheduleHost}
      />
    </FlexCol>
  );
};
